import React, {useEffect, useMemo, useState} from 'react';
import { GoogleMap, Marker, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import {ActionButton} from "../../../../designSystem/components/ActionButton";

const calculationMeanLocation = (locations) => {
  let sumLat = 0;
  let sumLng = 0;
  locations.forEach(location => {
    if (location.lat && location.lng) {
      sumLat += location.lat;
      sumLng += location.lng;
    }
  })
  return {
    lat: sumLat / locations.length,
    lng: sumLng / locations.length
  };
}
export const MapWithMarkers = ({ locations, onClickMarker }) => {
  const [map, setMap] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Google Maps API Key (Replace with your own API key)
  const apiKey = 'AIzaSyD3M4ZvtgjmFMQ5e7Fe--oOa1oJNJWESmQ';

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
  });

  // Get User's Current Location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          setLocationError('請開啟LINE之定位權限');
          console.error('Error fetching user location:', error)
        }
      );
    } else {
      setLocationError('請開啟LINE之定位權限');
      console.error('Geolocation is not supported by this browser.');
    }
  }, []);

  useEffect(() => {
    const defaultCenter = calculationMeanLocation(locations);
    setMapCenter(defaultCenter);
  }, [locations]);

  // normalize locations -> always number
  const validLocations = useMemo(() => {
    if (!Array.isArray(locations)) return [];
    return locations
      .map((loc) => ({
        ...loc,
        lat: Number(loc.lat),
        lng: Number(loc.lng),
      }))
      .filter((loc) => Number.isFinite(loc.lat) && Number.isFinite(loc.lng));
  }, [locations]);

  // Map Container Styling
  const mapContainerStyle = {
    width: '100%',
    height: 'calc((100vh - 225px) / 2)',
  };
  // Fit bounds whenever markers change (and map is ready)
  useEffect(() => {
    if (!map || !window.google) return;

    const points = [
      ...validLocations.map((l) => ({ lat: l.lat, lng: l.lng })),
    ];

    if (points.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));

    // 如果只有一個點，fitBounds 會 zoom 很近，這裡做個合理 zoom
    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(15);
      return;
    }

    map.fitBounds(bounds, 60); // 60 是 padding，讓 marker 不貼邊
  }, [map, validLocations]);

  if (!isLoaded) {
    return <div className="text-center text-gray-500">載入中...</div>;
  }

  return (
    <div className="flex flex-col items-center">
      {locationError && (
        <div className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded mb-2 text-sm">
          {locationError}
        </div>
      )}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={10}
        center={mapCenter}
        onLoad={(m) => setMap(m)}
        onUnmount={() => setMap(null)}
      >
        {/* User Location Marker */}
        {userLocation && (
          <Marker
            position={userLocation}
            label="您"
            icon={{
              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            }}
          />
        )}

        {/* Locations Markers */}
        {locations.map((location, index) => (
          <Marker
            key={index}
            position={location}
            label={''}
            onClick={() => {
              const position = {lat: location.lat, lng: location.lng}
              setMapCenter(position)
              setSelectedLocation({...location, position})
              onClickMarker(location.id)
            }}
          />
        ))}

        {/* InfoWindow for Selected Marker */}
        {selectedLocation && (
          <InfoWindow
            position={selectedLocation.position}
            onCloseClick={() => setSelectedLocation(null)} // Close the InfoWindow
          >
            <div className="flex flex-col gap-2">
              <h2 className="font-bold">
                {selectedLocation.name}
              </h2>
              <p>
                {selectedLocation.address || 'No description available.'}
              </p>
              <ActionButton
                onClick={() => {
                  const address = encodeURIComponent(selectedLocation.address);
                  const url = `https://www.google.com/maps/search/?api=1&query=${address}`;
                  window.open(url, '_blank');
                }}
                label="開啓Google Map"
              />
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};
